import { useEffect, useRef, useCallback } from 'react';
import L, { Map as LeafletMap } from 'leaflet';
import { mapService } from '../services/mapService';
import { getRangeColor } from '../utils/mapUtils';

/**
 * Hook pour gérer les couches de la carte (massifs, WMS)
 */
export const useMapLayers = (
  mapInstance: React.RefObject<LeafletMap | null>,
  showRanges: boolean,
  selectedActivity?: string
) => {
  const rangesLayer = useRef<L.LayerGroup | null>(null);
  const skiTouringWmsLayer = useRef<L.TileLayer.WMS | null>(null);
  const pixelCheckCanvas = useRef<HTMLCanvasElement | null>(null);
  const pixelCheckCtx = useRef<CanvasRenderingContext2D | null>(null);

  // Charger et afficher les massifs
  const loadAndDisplayRanges = useCallback(async (): Promise<void> => {
    if (!mapInstance.current) return;

    try {
      const geojsonData = await mapService.fetchRangesGeoJSON();

      if (rangesLayer.current) {
        mapInstance.current.removeLayer(rangesLayer.current);
      }

      rangesLayer.current = L.layerGroup();

      const geoJsonLayer = L.geoJSON(geojsonData, {
        style: (feature?: any) => {
          const color = feature ? getRangeColor(feature.properties.id) : '#2196F3';
          return {
            color: color,
            weight: 3,
            opacity: 1.0,
            fillColor: color,
            fillOpacity: 0.35
          };
        },
        pane: 'rangesPane',
        onEachFeature: (feature: any, layer: L.Layer) => {
          const color = getRangeColor(feature.properties.id);

          const popupContent = `
            <div>
              <h4>🏔️ ${feature.properties.title}</h4>
              <p><strong>Routes:</strong> ${feature.properties.routeCount}</p>
              ${feature.properties.summary ? `<p>${feature.properties.summary}</p>` : ''}
            </div>
          `;

          layer.bindPopup(popupContent);

          layer.on('mouseover', () => {
            (layer as L.Path).setStyle({
              weight: 4,
              opacity: 1.0,
              fillOpacity: 0.5
            });
          });

          layer.on('mouseout', () => {
            (layer as L.Path).setStyle({
              color: color,
              weight: 3,
              opacity: 1.0,
              fillColor: color,
              fillOpacity: 0.35
            });
          });
        },
        coordsToLatLng: (coords: [number, number]) => {
          const lng = coords[0] / 20037508.34 * 180;
          let lat = coords[1] / 20037508.34 * 180;
          lat = 180 / Math.PI * (2 * Math.atan(Math.exp(lat * Math.PI / 180)) - Math.PI / 2);
          return L.latLng(lat, lng);
        }
      });

      rangesLayer.current?.addLayer(geoJsonLayer);

      if (showRanges) {
        mapInstance.current.addLayer(rangesLayer.current);
      }
    } catch (error) {
      // Erreur silencieuse pour le chargement des massifs
    }
  }, [mapInstance, showRanges]);

  // Lire le pixel sous la souris dans la couche WMS
  const getWmsPixelData = useCallback((e: L.LeafletMouseEvent): Uint8ClampedArray | null => {
    if (!mapInstance.current) return null;

    const wmsPane = mapInstance.current.getPane('skiWmsPane');
    if (!wmsPane) return null;

    const tiles = Array.from(wmsPane.querySelectorAll('img')) as HTMLImageElement[];

    for (const tile of tiles) {
      const rect = tile.getBoundingClientRect();
      const x = e.originalEvent.clientX - rect.left;
      const y = e.originalEvent.clientY - rect.top;

      if (x >= 0 && x < rect.width && y >= 0 && y < rect.height) {
        if (!pixelCheckCanvas.current) {
          pixelCheckCanvas.current = document.createElement('canvas');
          pixelCheckCtx.current = pixelCheckCanvas.current.getContext('2d', { willReadFrequently: true });
        }

        const canvas = pixelCheckCanvas.current;
        const ctx = pixelCheckCtx.current;
        if (!ctx) return null;

        if (canvas.width !== tile.naturalWidth || canvas.height !== tile.naturalHeight) {
          canvas.width = tile.naturalWidth;
          canvas.height = tile.naturalHeight;
        }

        try {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(tile, 0, 0);

          const scaleX = tile.naturalWidth / rect.width;
          const scaleY = tile.naturalHeight / rect.height;
          const pixelX = Math.floor(x * scaleX);
          const pixelY = Math.floor(y * scaleY);

          const pixelData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
          return pixelData;
        } catch (err) {
          return null;
        }
      }
    }
    return null;
  }, [mapInstance]);

  // Gestionnaire de mouvement de souris pour la couche WMS
  const handleWmsMouseMove = useCallback((e: L.LeafletMouseEvent) => {
    const pixelData = getWmsPixelData(e);
    const isOverTrace = pixelData && pixelData[3] > 0;

    if (mapInstance.current) {
      const mapContainer = mapInstance.current.getContainer();
      mapContainer.style.cursor = isOverTrace ? 'pointer' : '';
    }
  }, [getWmsPixelData, mapInstance]);

  // Effet pour charger les massifs
  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.whenReady(() => {
      loadAndDisplayRanges();
    });
  }, [mapInstance, loadAndDisplayRanges]);

  // Effet pour afficher/masquer les massifs
  useEffect(() => {
    if (!mapInstance.current || !rangesLayer.current) return;

    if (showRanges) {
      mapInstance.current.addLayer(rangesLayer.current);
    } else {
      mapInstance.current.removeLayer(rangesLayer.current);
    }
  }, [showRanges, mapInstance]);

  // Effet pour afficher/masquer la couche WMS des traces de ski de randonnée
  useEffect(() => {
    if (!mapInstance.current) return;

    const map = mapInstance.current;

    if (selectedActivity === 'skitouring') {
      if (!skiTouringWmsLayer.current) {
        skiTouringWmsLayer.current = L.tileLayer.wms('https://api.ensg.eu/geoserver/yeti/wms', {
          layers: 'yeti:TRACERANDOHIVERNALE',
          format: 'image/png',
          transparent: true,
          version: '1.3.0',
          crs: L.CRS.EPSG3857,
          attribution: '© ENSG - Traces randonnée hivernale',
          pane: 'skiWmsPane',
          crossOrigin: 'anonymous'
        });
      }
      if (!map.hasLayer(skiTouringWmsLayer.current)) {
        skiTouringWmsLayer.current.addTo(map);
      }
      map.on('mousemove', handleWmsMouseMove);
    } else {
      if (skiTouringWmsLayer.current && map.hasLayer(skiTouringWmsLayer.current)) {
        map.removeLayer(skiTouringWmsLayer.current);
      }
      map.off('mousemove', handleWmsMouseMove);
      const mapContainer = map.getContainer();
      mapContainer.style.cursor = '';
    }

    return () => {
      map.off('mousemove', handleWmsMouseMove);
      const mapContainer = map.getContainer();
      mapContainer.style.cursor = '';
    };
  }, [selectedActivity, mapInstance, handleWmsMouseMove]);

  return {
    rangesLayer,
    skiTouringWmsLayer,
  };
};
